import OpusMediaRecorder from "opus-media-recorder/OpusMediaRecorder.umd.js";
import OggOpusEncoder from "opus-media-recorder/OggOpusEncoder.wasm?url";
import WebMOpusEncoder from "opus-media-recorder/WebMOpusEncoder.wasm?url";
import EncoderWorker from "opus-media-recorder/encoderWorker.umd.js?worker";
import { makeObservable, observable, computed, runInAction, action } from "mobx";

export class Recorder {
  paused = false;
  _duration = 0;
  #durationInterval: number | null = null;
  #stream: MediaStream;
  #recorder: MediaRecorder;
  #blobs: Blob[] = [];

  constructor(stream: MediaStream) {
    makeObservable(this, { _duration: observable, paused: observable, duration: computed, pause: action, resume: action, stop: action });
    this.#stream = stream;
    this.#recorder = new OpusMediaRecorder(
      stream,
      { mimeType: "audio/ogg; codecs=opus" },
      {
        encoderWorkerFactory: EncoderWorker,
        OggOpusEncoderWasmPath: OggOpusEncoder,
        WebMOpusEncoderWasmPath: WebMOpusEncoder,
      }
    );
    try {
      this.#recorder.addEventListener("dataavailable", (event) => this.#blobs.push(event.data));
      this.#recorder.start();
      this.#durationInterval = window.setInterval(() => runInAction(() => this._duration++), 1000);
    } catch (error) {
      runInAction(() => (this._duration = 0));
      this.#durationInterval && window.clearInterval(this.#durationInterval);
    }
  }

  get duration() {
    const hours = Math.floor(this._duration / 3600);
    const minutes = Math.floor((this._duration - hours * 3600) / 60);
    const seconds = this._duration - hours * 3600 - minutes * 60;
    return `${hours ? `${hours}:` : ""}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.#recorder?.pause();
    this.#durationInterval && window.clearInterval(this.#durationInterval);
  }
  resume() {
    if (!this.paused) return;
    this.paused = false;
    this.#recorder?.resume();
    this.#durationInterval = window.setInterval(() => runInAction(() => this._duration++), 1000);
  }

  async stop() {
    runInAction(() => {
      this.paused = false;
    });
    this.#durationInterval && window.clearInterval(this.#durationInterval);
    const promise = new Promise<void>((resolve) => this.#recorder.addEventListener("stop", () => resolve()));
    try {
      this.#recorder.stop();
      this.#stream.getTracks().forEach((track) => track.stop());
      const type = this.#recorder?.mimeType || "audio/ogg; codecs=opus";
      await promise;
      const blob = new Blob(this.#blobs, { type });
      return new File([blob], `Tuvis_Recording_${new Date().toISOString()}.${type.split(";")[0].split("/")[1]}`, { type });
    } catch {
      return null;
    }
  }
}
